import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Menu from 'antd/lib/menu'
import Badge from 'antd/lib/badge'
import { I18n } from 'aws-amplify'
import MenuIcons from './MenuIcons'
import { Link, useLocation } from 'react-router-dom'
import AdminIcon from '../../../assets/images/manage.svg'
import ClientsIcon from '../../../assets/images/clients.png'
import TenantsIcon from '../../../assets/images/tenants.png'
import PropertiesIcon from '../../../assets/images/properties.svg'
import './SideMenu.css'
import { isTeamsToolEnabled } from '../../../lib/helpers'

import {
    DASHBOARD,
    DOCUMENTS,
    NOTIFICATIONS,
    CALENDAR,
    TRAINING_PROGRAMS,
    PROCEDURES,
    REPORTS,
    RESOURCES,
    EVACUATION_ASSISTANCE,
    LIBRARY,
    PLAYLISTS,
    VIDEOS,
    QUIZZES,
    SUPPORT,
    MAP,
    FLOOR_PLANS,
    ADDITIONAL_RESOURCES,
    MANAGE,
    ACKNOWLEDGMENT,
    PROGRAM_REPORTS,
    TEAMS,
    ADMIN,
    CLIENTS,
    PROPERTIES,
    TENANTS,
    BUILDING,
} from '../../../constants/text'

const { SubMenu } = Menu

const SideMenu = () => {
    const modules = useSelector((state) => state.app.building.settings?.modules)
    const roles = useSelector((state) => state.app.user.roles)
    const requests = useSelector((state) => state.floorWarden.floorWardenRequests)
    const buildingpk = useSelector((state) => state.app.building.pk)
    const propertiespk = useSelector((state) => state.app.property.pk)
    const tenantpk = useSelector((state) => state.app.tenant.pk)
    const isSuperAdmin = roles.includes('SUPER_ADMIN')
    const isPropertyAdmin = roles.includes('PROPERTY_ADMIN')
    const isBuildingAdmin = roles.includes('BUILDING_ADMIN')
    const isTenantAdmin = roles.includes('TENANT_ADMIN')
    const isFSD = roles.includes('FIRE_SAFETY_DIRECTOR')
    const isAdmin = isPropertyAdmin || isBuildingAdmin || isTenantAdmin || isSuperAdmin

    const location = useLocation()
    const [selectedKeys, setSelectedKeys] = useState([])
    const [openKeys, setOpenKeys] = useState(['resources'])

    // Set the initially selected menu item based on user role and current path
    useEffect(() => {
        const path = location.pathname

        // Special handling for admin users - highlight administration tab for root/admin paths
        if ((isPropertyAdmin || isBuildingAdmin || isTenantAdmin || isFSD) &&
            (path === '/' || path.includes('/admin') ||
                path.includes('/admin/properties') ||
                path.includes('/admin/tenants') ||
                path.includes('/admin/buildings'))) {
            setSelectedKeys(['administration'])
            return
        }

        // For admin paths, handle specific submenu selections for superadmin
        if (path.includes('/admin/clients')) {
            setSelectedKeys(['clients'])
        } else if (path.includes('/admin/properties')) {
            setSelectedKeys(['properties'])
        } else if (path.includes('/admin/tenants')) {
            setSelectedKeys(['tenants'])
        } else if (path.includes('/admin/buildings')) {
            setSelectedKeys(['properties'])
        }
        // Handle dashboard path for all users including superadmin
        else if (path === '/dashboard') {
            // For Dashboard page - for all users including superadmin
            setSelectedKeys(['dashboard'])
        }
        // For superadmin on root path, send to dashboard
        else if (isSuperAdmin && path === '/') {
            setSelectedKeys(['dashboard'])
        }
        // For other admins and the path includes admin or is root
        else if (isAdmin && (path.includes('/admin') || path === '/')) {
            setSelectedKeys(['administration'])
        } else if (path === '/trainingportal') {
            // For Training Programs page
            setSelectedKeys(['training'])
        } else if (path === '/') {
            // For non-admin users on root path
            if (!isAdmin) {
                // Check if dashboard module is enabled
                const isDashboardEnabled = modules?.some(
                    module => module.key === 'dashboard' && module.enabled
                )

                if (isDashboardEnabled) {
                    // If dashboard is enabled, highlight dashboard
                    setSelectedKeys(['dashboard'])
                } else {
                    // Otherwise, highlight training programs
                    setSelectedKeys(['training'])
                }
            } else {
                // For admins on root path, highlight administration 
                // This should already be handled by the first condition for non-superadmin admins
                setSelectedKeys(['administration'])
            }
        } else if (path.includes('/training')) {
            setSelectedKeys(['training'])
        } else if (path.includes('/reports/programs/') ||
            path.includes('/reports/program/') ||
            path.includes('/reports/tenants') ||
            path.includes('/reports/acknowledgments')) {
            // For program reports, tenant reports, and acknowledgment reports - highlight administration
            setSelectedKeys(['administration'])
        } else if (path.includes('/reports')) {
            setSelectedKeys(['reports'])
        } else if (path.includes('/covid')) {
            setSelectedKeys(['covid'])
        } else if (path.includes('/procedures')) {
            setSelectedKeys(['procedures'])
        } else if (path.includes('/assistance')) {
            setSelectedKeys(['evacAssistance'])
        } else if (path.includes('/floorwarden')) {
            setSelectedKeys(['floorWarden'])
        } else if (path.includes('/teams')) {
            setSelectedKeys(['teams'])
        } else if (path.includes('/dsal')) {
            setSelectedKeys(['dsal'])
        } else if (path.includes('/calendar')) {
            setSelectedKeys(['calendar'])
        } else if (path.includes('/floorplans')) {
            setSelectedKeys(['floorPlans'])
        } else if (path.includes('/neighborhood')) {
            setSelectedKeys(['map'])
        } else if (path.includes('/documents')) {
            setSelectedKeys(['documents'])
        } else if (path.includes('/resources')) {
            setSelectedKeys(['additionalResources'])
        } else if (path.includes('/support')) {
            setSelectedKeys(['support'])
        } else if (path.includes('/library')) {
            setSelectedKeys(['library'])
        } else if (path.includes('/notifications')) {
            setSelectedKeys(['notifications'])
        } else {
            // For any other paths
            setSelectedKeys([])
        }
    }, [location.pathname, isAdmin, isSuperAdmin, isFSD, modules])

    // For superadmin, treat all modules as enabled
    const getModuleForSuperAdmin = () => ({ enabled: true })

    const menuCreator = (modules, role) => {
        const getModule = (module) => {
            // For superadmin, all modules are enabled regardless of property settings
            if (isSuperAdmin) {
                return getModuleForSuperAdmin()
            }
            let mod = modules && modules.find((m) => m.key === module)
            return mod ? mod : { enabled: false }
        }

        const isPropertyAdmin = role.includes('PROPERTY_ADMIN')
        const isBuildingAdmin = role.includes('BUILDING_ADMIN')
        const isTenantAdmin = role.includes('TENANT_ADMIN')
        const isClientAdmin = role.includes('CLIENT_ADMIN')
        const isSuperAdmin = role.includes('SUPER_ADMIN')
        const isFSDRole = role.includes('FIRE_SAFETY_DIRECTOR')

        // The Teams tool toggle ('teams' key, separate from Floor Warden) hides
        // the Teams entry for everyone — including super admins, who normally
        // see every module — matching the admin Teams tab, which is gated for
        // all roles.
        const teamsToolEnabled = isTeamsToolEnabled(modules)
        // FSD without any other admin role gets the restricted Teams-only view
        const isFSDOnly = isFSDRole && !isPropertyAdmin && !isBuildingAdmin && !isTenantAdmin && !isSuperAdmin

        if (isSuperAdmin) {
            return [
                {
                    key: 'administration',
                    title: ADMIN,
                    externalIcon: true,
                    icon: AdminIcon,
                    show: true,
                    subMenu: [
                        {
                            key: 'clients',
                            title: CLIENTS,
                            externalIcon: true,
                            icon: ClientsIcon,
                            link: '/admin/clients',
                            show: true,
                        },
                        {
                            key: 'properties',
                            title: PROPERTIES,
                            externalIcon: true,
                            icon: PropertiesIcon,
                            link: '/admin/properties',
                            show: true,
                        },
                        {
                            key: 'tenants',
                            title: TENANTS,
                            externalIcon: true,
                            icon: TenantsIcon,
                            link: '/admin/tenants',
                            show: true,
                        },
                        {
                            key: 'notifications',
                            title: NOTIFICATIONS,
                            link: '/notifications',
                            icon: 'notifications',
                            show: getModule('notifications').enabled,
                        },
                        {
                            key: 'library',
                            title: LIBRARY,
                            externalIcon: false,
                            link: '/library',
                            icon: 'library',
                            show: true,
                        },
                    ],
                },
                {
                    key: 'dashboard',
                    title: DASHBOARD,
                    link: '/dashboard',
                    icon: 'dashboard',
                    show: getModule('dashboard').enabled,
                },
                {
                    key: 'training',
                    title: TRAINING_PROGRAMS,
                    link: '/trainingportal',
                    icon: 'training',
                    show: getModule('trainingPrograms').enabled,
                },
                {
                    key: 'reports',
                    title: REPORTS,
                    link: '/reports',
                    icon: 'report',
                    show: true,
                },
                {
                    key: 'covid',
                    title: 'Contact Tracing',
                    link: '/covid',
                    icon: 'covid',
                    show: getModule('contactTracing').enabled,
                },
                {
                    key: 'procedures',
                    title: PROCEDURES,
                    link: '/procedures',
                    icon: 'procedures',
                    show: getModule('procedures').enabled,
                },
                {
                    key: 'evacAssistance',
                    title: EVACUATION_ASSISTANCE,
                    tooltip: EVACUATION_ASSISTANCE,
                    link: '/assistance',
                    icon: 'assistance',
                    show: getModule('evacuationAssistance').enabled,
                },
                {
                    // Replaces the Floor Warden manager in the left navigation
                    key: 'teams',
                    title: TEAMS,
                    link: '/teams',
                    icon: 'teams',
                    show: teamsToolEnabled,
                },
                {
                    key: 'dsal',
                    title: 'FW Certification',
                    link: '/dsal',
                    icon: 'floorWarden',
                    show: getModule('dsal').enabled,
                },
                {
                    key: 'calendar',
                    title: CALENDAR,
                    link: '/calendar',
                    icon: 'calendar',
                    show: getModule('calendar').enabled,
                },
                {
                    key: 'resources',
                    title: RESOURCES,
                    icon: 'resources',
                    show: getModule('floorPlans').enabled || getModule('map').enabled || getModule('documents').enabled || getModule('additionalResources').enabled,
                    subMenu: [
                        {
                            key: 'floorPlans',
                            title: FLOOR_PLANS,
                            icon: 'floorPlan',
                            link: '/floorplans',
                            show: getModule('floorPlans').enabled,
                        },
                        {
                            key: 'map',
                            title: MAP,
                            icon: 'map',
                            link: '/neighborhood',
                            show: getModule('map').enabled,
                        },
                        {
                            key: 'documents',
                            title: DOCUMENTS,
                            icon: 'documents',
                            link: '/documents',
                            show: getModule('documents').enabled,
                        },
                        {
                            key: 'additionalResources',
                            title: ADDITIONAL_RESOURCES,
                            tooltip: ADDITIONAL_RESOURCES,
                            icon: 'addResources',
                            link: '/resources',
                            show: getModule('additionalResources').enabled,
                        },
                    ],
                },
                {
                    key: 'support',
                    title: SUPPORT,
                    link: '/support',
                    icon: 'support',
                    show: getModule('support').enabled,
                },
            ]
        }

        return [
            {
                key: 'administration',
                title: ADMIN,
                externalIcon: true,
                icon: AdminIcon,
                link: isFSDOnly
                    ? `/admin/buildings/${buildingpk}#teams`
                    : isTenantAdmin
                        ? `/admin/tenants/${tenantpk}`
                        : isPropertyAdmin
                            ? `/admin/properties/${propertiespk}`
                            : isBuildingAdmin
                                ? `/admin/buildings/${buildingpk}`
                                : undefined,
                show: role.some(item => typeof item === 'string' && item.includes('_ADMIN')) || isFSDRole,
                subMenu: (isPropertyAdmin || isTenantAdmin || isBuildingAdmin || isFSDOnly) ? undefined : [
                    {
                        key: 'clients',
                        title: CLIENTS,
                        externalIcon: true,
                        icon: ClientsIcon,
                        link: '/admin/clients',
                        show: isClientAdmin || isSuperAdmin,
                    },
                    {
                        key: 'properties',
                        title: isBuildingAdmin ? BUILDING : PROPERTIES,
                        externalIcon: true,
                        icon: PropertiesIcon,
                        link: isBuildingAdmin
                            ? `/admin/buildings/${buildingpk}`
                            : '/admin/properties',
                        show: isBuildingAdmin || isSuperAdmin,
                    },
                    {
                        key: 'tenants',
                        title: TENANTS,
                        externalIcon: true,
                        icon: TenantsIcon,
                        link: '/admin/tenants',
                        show: isSuperAdmin,
                    },
                    {
                        key: 'notifications',
                        title: NOTIFICATIONS,
                        link: '/notifications',
                        icon: 'notifications',
                        show: getModule('notifications').enabled,
                    },
                    {
                        key: 'library',
                        title: LIBRARY,
                        externalIcon: false,
                        link: '/library',
                        icon: 'library',
                        show: isSuperAdmin,
                    },
                ],
            },
            {
                key: 'dashboard',
                title: DASHBOARD,
                link: '/dashboard',
                icon: 'dashboard',
                show: getModule('dashboard').enabled,
            },
            {
                key: 'training',
                title: TRAINING_PROGRAMS,
                link: '/trainingportal',
                icon: 'training',
                show: getModule('trainingPrograms').enabled,
            },
            {
                key: 'reports',
                title: REPORTS,
                link: '/reports',
                icon: 'report',
                show: true,
            },
            {
                key: 'covid',
                title: 'Contact Tracing',
                link: '/covid',
                icon: 'covid',
                show: getModule('contactTracing').enabled,
            },
            {
                key: 'procedures',
                title: PROCEDURES,
                link: '/procedures',
                icon: 'procedures',
                show: getModule('procedures').enabled,
            },
            {
                key: 'evacAssistance',
                title: EVACUATION_ASSISTANCE,
                tooltip: EVACUATION_ASSISTANCE,
                link: '/assistance',
                icon: 'assistance',
                show: getModule('evacuationAssistance').enabled,
            },
            {
                // Replaces the Floor Warden manager in the left navigation
                key: 'teams',
                title: TEAMS,
                link: '/teams',
                icon: 'teams',
                show: teamsToolEnabled,
            },
            {
                key: 'dsal',
                title: 'FW Certification',
                link: '/dsal',
                icon: 'floorWarden',
                show: getModule('dsal').enabled,
            },
            {
                key: 'calendar',
                title: CALENDAR,
                link: '/calendar',
                icon: 'calendar',
                show: getModule('calendar').enabled,
            },
            {
                key: 'resources',
                title: RESOURCES,
                icon: 'resources',
                show: getModule('floorPlans').enabled || getModule('map').enabled || getModule('documents').enabled || getModule('additionalResources').enabled,
                subMenu: [
                    {
                        key: 'floorPlans',
                        title: FLOOR_PLANS,
                        icon: 'floorPlan',
                        link: '/floorplans',
                        show: getModule('floorPlans').enabled,
                    },
                    {
                        key: 'map',
                        title: MAP,
                        icon: 'map',
                        link: '/neighborhood',
                        show: getModule('map').enabled,
                    },
                    {
                        key: 'documents',
                        title: DOCUMENTS,
                        icon: 'documents',
                        link: '/documents',
                        show: getModule('documents').enabled,
                    },
                    {
                        key: 'additionalResources',
                        title: ADDITIONAL_RESOURCES,
                        tooltip: ADDITIONAL_RESOURCES,
                        icon: 'addResources',
                        link: '/resources',
                        show: getModule('additionalResources').enabled,
                    },
                ],
            },
            {
                key: 'support',
                title: SUPPORT,
                link: '/support',
                icon: 'support',
                show: getModule('support').enabled,
            },
        ]
    }

    // Determine which submenus should be open based on the selected keys and current path
    const getOpenKeys = () => {
        const keys = [];
        const path = location.pathname;

        // Check if we're in a resources path
        if (path.includes('/floorplans') ||
            path.includes('/neighborhood') ||
            path.includes('/documents') ||
            path.includes('/resources')) {
            keys.push('resources');
        }

        // Check if we're in an admin path
        if (path.includes('/admin/clients') ||
            path.includes('/admin/properties') ||
            path.includes('/admin/tenants') ||
            path.includes('/admin/buildings') ||
            path.includes('/notifications') ||
            path.includes('/library')) {
            keys.push('administration');
        }

        // If a submenu item is selected, make sure its parent menu is open
        if (selectedKeys.includes('floorPlans') ||
            selectedKeys.includes('map') ||
            selectedKeys.includes('documents') ||
            selectedKeys.includes('additionalResources')) {
            if (!keys.includes('resources')) {
                keys.push('resources');
            }
        }

        // If admin submenu item is selected, make sure admin menu is open
        if (selectedKeys.includes('clients') ||
            selectedKeys.includes('properties') ||
            selectedKeys.includes('tenants') ||
            selectedKeys.includes('notifications') ||
            selectedKeys.includes('library') ||
            selectedKeys.includes('administration')) {
            if (!keys.includes('administration')) {
                keys.push('administration');
            }
        }

        // Don't default to resources open for dashboard view
        if (keys.length === 0 && (selectedKeys.includes('dashboard') || path === '/dashboard')) {
            return keys; // Empty array for dashboard - don't auto-open any submenu
        }

        // Default to resources open if no other keys are set
        if (keys.length === 0) {
            keys.push('resources');
        }

        return keys;
    };

    // Update openKeys when location or selectedKeys change
    useEffect(() => {
        setOpenKeys(getOpenKeys());
    }, [location.pathname, selectedKeys]);

    const handleOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    return (
        <Menu
            mode="inline"
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            selectedKeys={selectedKeys}
            style={{ borderRight: 0 }}
        >
            {menuCreator(isSuperAdmin ? null : modules, roles).map((item) => {
                // For superadmin, always show menu items regardless of show property
                const shouldShow = isSuperAdmin ? true : item.show;

                // Make sure dashboard uses consistent key name
                const itemKey = item.key === 'dashboard' ? 'dashboard' : item.key;

                return item.subMenu != null && shouldShow ? (
                    <SubMenu
                        key={itemKey}
                        title={
                            <span>
                                <MenuIcons id={item.icon} externalIcon={item.externalIcon} />
                                {I18n.get(item.title)}
                            </span>
                        }
                    >
                        {item.subMenu.map((sitem) => {
                            // For superadmin, always show submenu items
                            const shouldShowSubItem = isSuperAdmin ? true : sitem.show;
                            return (
                                shouldShowSubItem && (
                                    <Menu.Item key={sitem.key}>
                                        <MenuIcons id={sitem.icon} externalIcon={sitem.externalIcon} />
                                        {sitem.key === 'floorWarden' ? (
                                            <Badge count={requests.length} offset={[15, 0]}>
                                                <span className="menu-item-text">{I18n.get(sitem.title)}</span>
                                            </Badge>
                                        ) : (
                                            <span className="menu-item-text">{I18n.get(sitem.title)}</span>
                                        )}
                                        <Link to={sitem.link} />
                                    </Menu.Item>
                                )
                            )
                        })}
                    </SubMenu>
                ) : (
                    shouldShow && (
                        <Menu.Item key={itemKey} style={{ padding: 0 }}>
                            <MenuIcons id={item.icon} externalIcon={item.externalIcon} />
                            <span className="menu-item-text">{I18n.get(item.title)}</span>
                            <Link to={item.link} />
                        </Menu.Item>
                    )
                )
            })}
        </Menu>
    )
}

export default SideMenu